/**
 * Utility for formatting Trello card descriptions
 */

import { FormatUtil } from './formatters';

export class TrelloCardFormatter {
  /**
   * Format lead data for Trello card description
   */
  static formatLeadDescription(leadData: any): string {
    const sections: string[] = [];

    // Company Information
    sections.push('## 🏢 Company Information');
    this.addCompanySection(sections, leadData);

    // Scoring Information
    sections.push('## 📊 Scoring');
    this.addScoringSection(sections, leadData);

    // Detailed Metrics
    if (leadData.metrics) {
      sections.push('## 🎯 Detailed Metrics');
      this.addMetricsSection(sections, leadData.metrics);
    }

    // Contact Information
    if (this.hasContacts(leadData.topContacts)) {
      sections.push('## 👥 Key Contacts');
      this.addContactsSection(sections, leadData.topContacts);
    }

    // Footer
    this.addFooter(sections);

    return sections.join('\n\n');
  }

  private static addCompanySection(sections: string[], leadData: any): void {
    if (leadData.sector) {
      sections.push(`**Sector:** ${FormatUtil.formatSector(leadData.sector)}`);
    }

    if (leadData.companySize) {
      sections.push(`**Company Size:** ${FormatUtil.formatCompanySize(leadData.companySize)}`);
    }

    if (leadData.location) {
      sections.push(`**Location:** ${leadData.location}`);
    }
  }

  private static addScoringSection(sections: string[], leadData: any): void {
    if (leadData.averageScore !== undefined) {
      sections.push(`**Average Score:** ${FormatUtil.formatScore(leadData.averageScore)}`);
    }

    if (leadData.overallScore) {
      sections.push(`**Overall Assessment:** ${FormatUtil.formatScore(leadData.overallScore.score)}`);
      sections.push(`**Reason:** ${leadData.overallScore.reason}`);

      if (leadData.overallScore.description &&
          leadData.overallScore.description !== leadData.overallScore.reason) {
        sections.push(`**Description:** ${leadData.overallScore.description}`);
      }
    }
  }

  private static addMetricsSection(sections: string[], metrics: any): void {
    Object.entries(metrics).forEach(([key, metric]: [string, any]) => {
      const label = FormatUtil.getMetricLabel(key);
      sections.push(`**${label}:** ${FormatUtil.formatScore(metric.score)}`);
      sections.push(`*${metric.reason}*`);

      if (metric.description && metric.description !== metric.reason) {
        sections.push(`${metric.description}`);
      }

      sections.push(''); // Add spacing between metrics
    });
  }

  private static hasContacts(topContacts: any): boolean {
    return topContacts &&
           topContacts.contactList &&
           Array.isArray(topContacts.contactList) &&
           topContacts.contactList.length > 0;
  }

  private static addContactsSection(sections: string[], topContacts: any): void {
    if (topContacts.description) {
      sections.push(`*${topContacts.description}*`);
      sections.push('');
    }

    topContacts.contactList.forEach((contact: any, index: number) => {
      sections.push(`**Contact ${index + 1}: ${contact.name}**`);
      sections.push(`**Role:** ${contact.role}`);

      if (contact.bestGuessEmail) {
        sections.push(`**Email:** ${contact.bestGuessEmail}`);
      }

      if (contact.bestGuessPhone) {
        sections.push(`**Phone:** ${contact.bestGuessPhone}`);
      }

      if (contact.reason) {
        sections.push(`**Why this contact:** ${contact.reason}`);
      }

      if (contact.conversationStarter) {
        sections.push(`**Conversation starter:** ${contact.conversationStarter}`);
      }

      sections.push(''); // Add spacing between contacts
    });
  }

  private static addFooter(sections: string[]): void {
    sections.push('---');
    sections.push(`*Transferred from Lead Gen UI on ${FormatUtil.formatDate(new Date())}*`);
  }
}